import { Button } from '@ordo/ui/button'
import { Card } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { meProfileQueryKey } from '@/api/me'

/**
 * Email-change verification landing page.
 *
 *   /verify-email-change?token=<token>
 *
 * Reached from the verification email sent to the NEW address by
 * POST /api/me/email-change. On load the page POSTs the token to
 * /api/me/email-change/confirm, which updates the sign-in email.
 *
 * Registered OUTSIDE the auth wrappers (like /accept-invite) so the
 * link never loses its token to a redirect; the confirm endpoint
 * itself requires a session, and the 401 case renders a "sign in
 * first, then open the link again" state.
 */

type Status = 'loading' | 'success' | 'unauthenticated' | 'error'

export function VerifyEmailChangePage() {
  const [params] = useSearchParams()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<Status>('loading')
  const [message, setMessage] = useState<string | null>(null)
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null)

  useEffect(() => {
    const token = params.get('token')
    if (!token) {
      setMessage('This link is missing its verification token. Request a new one from Settings.')
      setStatus('error')
      return
    }
    let cancelled = false
    void fetch('/api/me/email-change/confirm', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (cancelled) {
          return
        }
        const body = (await res.json().catch(() => ({}))) as { email?: string; message?: string }
        if (res.ok && body.email) {
          setConfirmedEmail(body.email)
          setStatus('success')
          // The sign-in email changed — drop the cached profile.
          void queryClient.invalidateQueries({ queryKey: meProfileQueryKey })
          return
        }
        if (res.status === 401) {
          setStatus('unauthenticated')
          return
        }
        setMessage(body.message ?? 'This link is invalid or has expired.')
        setStatus('error')
      })
      .catch(() => {
        if (!cancelled) {
          setMessage('Something went wrong confirming your email. Try the link again.')
          setStatus('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [params, queryClient])

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md p-6">
        {status === 'loading' && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-9 w-32" />
          </div>
        )}
        {status === 'success' && (
          <div className="flex flex-col gap-3">
            <h1 className="text-lg font-semibold text-foreground">Email updated</h1>
            <p className="text-sm text-muted-foreground">
              Your sign-in email is now <span className="font-medium text-foreground">{confirmedEmail}</span>. Use it
              the next time you sign in — if you get signed out, sign back in with the new address.
            </p>
            <div className="mt-2 flex gap-2">
              <Button asChild>
                <Link to="/settings/account">Back to settings</Link>
              </Button>
            </div>
          </div>
        )}
        {status === 'unauthenticated' && (
          <div className="flex flex-col gap-3">
            <h1 className="text-lg font-semibold text-foreground">Sign in to confirm</h1>
            <p className="text-sm text-muted-foreground">
              You need to be signed in (with your current email) to confirm this change. Sign in, then open the link
              from your email again.
            </p>
            <div className="mt-2">
              <Button asChild>
                <Link to="/signin">Sign in</Link>
              </Button>
            </div>
          </div>
        )}
        {status === 'error' && (
          <div className="flex flex-col gap-3">
            <h1 className="text-lg font-semibold text-foreground">Couldn&apos;t confirm your email</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">
              You can request a new verification link from Settings &rarr; Account &rarr; Change email.
            </p>
            <div className="mt-2">
              <Button asChild variant="outline">
                <Link to="/settings/account">Go to settings</Link>
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
